import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchListings, fetchListingById } from "@/lib/api/fetchListings";
import { Listing, ListingFilters, ListingCore } from "@/lib/types";

/**
 * Hook to fetch listings with filters
 * @param filters Optional filters to apply
 */
export const useListings = (filters: ListingFilters = {}) => {
  console.log("Fetching listings with filters:", filters);
  // Directly use filters as apiFilters, as useListingFilters already handles
  // the conversion from UI selections and removes undefined properties.
  const apiFilters = filters;

  return useQuery({
    queryKey: ["listings", apiFilters],
    queryFn: async () => {
      const result = await fetchListings(apiFilters);
      return result; // Directly return result, as fetchListings already returns { properties, pagination }
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch a single listing by ID
 * @param id The listing ID
 */
export const useListing = (id: string) => {
  console.log("Fetching listing with ID:", id);

  return useQuery({
    queryKey: ["listing", id],
    queryFn: async () => {
      try {
        const result = await fetchListingById(id);
        console.log("Raw API response for single listing:", result);

        let listingData: Listing | undefined;
        let imagesData: any[] = [];
        let attributesData: any[] = [];
        let propertyTypeData: any | undefined; // Declare propertyTypeData here

        // Case 1: result is an ApiResponse structure
        if (
          result &&
          typeof result === "object" &&
          "status" in result &&
          result.status === "success" &&
          "data" in result
        ) {
          const apiResponseData = result.data;
          listingData = (apiResponseData as any)?.listing || apiResponseData;
          imagesData =
            (apiResponseData as any)?.images ||
            (listingData as any)?.images ||
            [];
          attributesData =
            (apiResponseData as any)?.attributes ||
            (listingData as any)?.attributes ||
            [];
          propertyTypeData = (apiResponseData as any)?.propertyType; // Assign propertyTypeData here
        }
        // Case 2: result is directly the Listing object (inconsistent API behavior)
        else if (
          result &&
          typeof result === "object" &&
          "id" in result &&
          "title" in result
        ) {
          listingData = result as unknown as Listing;
          imagesData = (listingData as any)?.images || [];
          attributesData = (listingData as any)?.attributes || [];
        }

        if (listingData && listingData.id) {
          console.log("Normalized listing data:", listingData);
          console.log("Normalized images data:", imagesData);
          console.log("Normalized attributes data:", attributesData);

          const coreListing: ListingCore = listingData; // Treat listingData as ListingCore for clarity

          const finalListing: Listing = {
            ...coreListing,
            isFeatured:
              coreListing.is_featured === 1 || Boolean(coreListing.is_featured),
            isBoosted:
              coreListing.is_boosted === 1 || Boolean(coreListing.is_boosted),
            listingType: coreListing.listing_type,
            location: {
              address: coreListing.address || "",
              city_id: coreListing.city_id,
              city_name: coreListing.city_name || coreListing.city?.name || "",
              city: coreListing.city, // Assign the full City object if available
            },
            attributes: attributesData,
            images: imagesData,
            propertyType: propertyTypeData, // Use the declared propertyTypeData
            featured:
              coreListing.is_featured === 1 || Boolean(coreListing.is_featured), // Ensure 'featured' is set
          };
          console.log("Final normalized listing object:", finalListing);
          return finalListing;
        } else {
          throw new Error(
            "Failed to fetch listing or invalid response format."
          );
        }
      } catch (error) {
        console.error("Error fetching listing:", error);
        throw error;
      }
    },
    enabled: !!id, // Only run the query if id is not empty or null
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry twice before failing
  });
};

// For backward compatibility, re-export the hooks from the API file
export { useListings as useAPIListings, useListing as useAPIListing };
